import React from 'react';
import { theme, sealColors } from '../../theme';
import { useLanguage } from '../../i18n/useLanguage';
import { SovRadarChart } from './SovRadarChart';
import type {
  SovScoreResult,
  IstSelection,
  SovWeights,
  WorkplaceLevel,
  AggregatedProvider,
  SealDefinition,
  DeploymentModel,
  SovObjective,
} from '../../types/workplace';

interface IstScoreStepProps {
  istScore: SovScoreResult;
  istSelections: Record<string, IstSelection>;
  sovWeights: SovWeights;
  levels: WorkplaceLevel[];
  aggregatedProviders: AggregatedProvider[];
  sealDefinitions: SealDefinition[];
  sovObjectives: SovObjective[];
  onContinue: () => void;
  onBack: () => void;
}

const DEPLOYMENT_LABEL_KEYS: Record<DeploymentModel, string> = {
  saas: 'workplace.deployment.saas',
  'hosted-eu': 'workplace.deployment.hostedEu',
  'hosted-non-eu': 'workplace.deployment.hostedNonEu',
  'self-hosted': 'workplace.deployment.selfHosted',
};

export const IstScoreStep: React.FC<IstScoreStepProps> = ({
  istScore,
  istSelections,
  levels,
  sealDefinitions,
  sovObjectives,
  onContinue,
  onBack,
}) => {
  const { t } = useLanguage();

  const sealDef = istScore.sealLevel !== null
    ? sealDefinitions.find(s => s.level === istScore.sealLevel) ?? null
    : null;
  const sealColor = istScore.sealLevel !== null ? sealColors[istScore.sealLevel] ?? theme.muted : theme.muted;

  const allSubPoints = levels.flatMap(l =>
    l.subPoints.map(sp => ({ spTitle: sp.title, spId: sp.id, spType: sp.type, levelTitle: l.title }))
  );

  return (
    <div style={{ maxWidth: 750, margin: '0 auto', padding: 16 }}>
      <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: theme.text }}>
        {t('workplace.istScore.title')}
      </h2>

      {/* Overall Score */}
      {istScore.overallScore !== null ? (
        <div style={{ textAlign: 'center', margin: '20px 0' }}>
          <div style={{ fontSize: 48, fontWeight: 800, color: sealColor }}>
            {istScore.overallScore.toFixed(1)}%
          </div>
          {sealDef && (
            <div style={{ marginTop: 8 }}>
              <span style={{
                display: 'inline-block', padding: '4px 12px', borderRadius: 4,
                background: sealColor, color: '#fff', fontWeight: 700, fontSize: 14,
              }}>
                {t('workplace.istScore.sealLevel')}: {sealDef.name}
              </span>
              <p style={{ fontSize: 13, color: theme.muted, marginTop: 6 }}>{sealDef.definition}</p>
            </div>
          )}
        </div>
      ) : (
        <p style={{ textAlign: 'center', fontSize: 16, color: theme.muted, margin: '24px 0' }}>
          {t('workplace.istScore.noScore')}
        </p>
      )}

      {/* Radar Chart */}
      {istScore.overallScore !== null && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 8 }}>
            {t('workplace.istScore.objectives')}
          </h3>
          <SovRadarChart istScores={istScore.perObjective} sovObjectives={sovObjectives} />
        </div>
      )}

      {/* Breakdown Table */}
      <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.text, marginBottom: 8 }}>
        {t('workplace.istScore.breakdown')}
      </h3>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: theme.cardBg, textAlign: 'left' }}>
              <th style={{ padding: '8px 10px', borderBottom: `1px solid ${theme.border}` }}>Feature Group</th>
              <th style={{ padding: '8px 10px', borderBottom: `1px solid ${theme.border}` }}>Provider</th>
              <th style={{ padding: '8px 10px', borderBottom: `1px solid ${theme.border}` }}>Deployment</th>
              <th style={{ padding: '8px 10px', borderBottom: `1px solid ${theme.border}`, textAlign: 'right' }}>SOV-Score</th>
            </tr>
          </thead>
          <tbody>
            {allSubPoints.map(({ spId, spTitle, spType }) => {
              const sel = istSelections[spId];
              const fg = istScore.perFeatureGroup[spId];
              const isOps = spType === 'operations-scoring';
              return (
                <tr key={spId}>
                  <td style={{ padding: '6px 10px', borderBottom: `1px solid ${theme.border}` }}>{spTitle}</td>
                  <td style={{ padding: '6px 10px', borderBottom: `1px solid ${theme.border}` }}>
                    {isOps ? (fg?.sovScore != null ? 'Operations Scoring' : '—') : (sel?.provider ?? '—')}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: `1px solid ${theme.border}` }}>
                    {isOps ? '—' : (sel?.deployment ? t(DEPLOYMENT_LABEL_KEYS[sel.deployment] as any) : '—')}
                  </td>
                  <td style={{ padding: '6px 10px', borderBottom: `1px solid ${theme.border}`, textAlign: 'right', fontWeight: 600 }}>
                    {fg?.sovScore !== null && fg?.sovScore !== undefined ? `${fg.sovScore.toFixed(1)}%` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Navigation Buttons */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        <button
          onClick={onBack}
          style={{
            padding: '10px 24px', border: `1px solid ${theme.border}`, background: '#fff',
            color: theme.text, fontWeight: 700, fontSize: 15, borderRadius: 4, cursor: 'pointer',
          }}
        >
          {t('workplace.istScore.back')}
        </button>
        <button
          onClick={onContinue}
          style={{
            padding: '10px 24px', border: `1px solid ${theme.primaryBorder}`,
            background: theme.primarySoft, color: theme.text,
            fontWeight: 700, fontSize: 15, borderRadius: 4, cursor: 'pointer',
          }}
        >
          {t('workplace.istScore.continue')}
        </button>
      </div>
    </div>
  );
};
